import { Grid, Stack } from '@mui/material';
import { Text, VSpacer } from '@/components/DesignSystem';
import React, { useState } from 'react';
import { ApiRetailer } from '@api/interfaces';
import { RetailerEndpoint } from '@api/endpoints';
import {
  BrandColorModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/BrandColorModal';
import ConfirmModal from '@/components/shared/ConfirmModal';
import { ImageModal } from '@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/ImageModal';
import { WebsiteModal } from '@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/WebsiteModal';
import {
  PropertyModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/PropertyModal';
import { formatPhoneNumber, getOriginalFileName } from '@shared/utilities';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { UtilityApi } from '@/utilities/api/UtilityApi';
import { SmsApi } from "@/utilities/api/SmsApi";
import { AppConfig } from "@/constants/AppConfig";
import {
  BrandAssetsCard,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/BrandAssetsCard";
import {
  BrandAssetsCardGroup,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/BrandAssetsCardGroup";
import {
  BrandAssetsCardGroupItem,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/BrandAssets/BrandAssetsCardGroupItem";
import { useQuery } from "react-query";
import { QueryKeys } from "@/constants/QueryKeys";
import { TwilioConfigApi } from "@/utilities/api/TwilioConfigApi";
import { isEmpty } from "lodash";
import { TwilioDetails } from "@/pages/Admin/HierarchyOfRetailers/Twilio/TwilioDetails";
import { DescriptionCard } from "@/pages/Admin/HierarchyOfRetailers/Retailer/DescriptionCard";
import {
  ProgramSectionHeader,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/ProgramSectionHeader";
import {
  getMostRelevantProgram,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers";
import { RewardsProgramType } from "@shared/enums/RewardsProgramType";

interface BrandAssetInputsProps {
  description?: string | null,
  onChange: (updates: RetailerEndpoint.Update.Request) => void,
  retailer: ApiRetailer,
}

export const BrandAssetInputs = ({
  description,
  onChange,
  retailer,
}: BrandAssetInputsProps) => {
  const { openSnackbar } = useSnackbar();
  const agronomyProgramName = getMostRelevantProgram(
    retailer.rewardsPrograms, RewardsProgramType.Agronomy,
  )?.name ?? 'Rewards & Loyalty Program';

  const { data: existingTwilioConfig } = useQuery(
    [QueryKeys.GET_RETAILER_TWILIO_CONFIG, retailer.id],
    async () => TwilioConfigApi.getByRetailerId(retailer.id),
  );
  
  const showMessagingTelephoneInput = isEmpty(existingTwilioConfig);

  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showLogomarkModal, setShowLogomarkModal] = useState(false);
  const [showBrandColorModal, setShowBrandColorModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showAppNameModal, setShowAppNameModal] = useState(false);
  const [showAppIconModal, setShowAppIconModal] = useState(false);
  const [showCampaignImageModal, setShowCampaignImageModal] = useState(false);
  const [showSmsPhoneModal, setShowSmsPhoneModal] = useState(false);
  const [showShortUrlModal, setShowShortUrlModal] = useState(false);
  const [showRemoveLogoDialog, setShowRemoveLogoDialog] = useState(false);
  const [showRemoveLogomarkDialog, setShowRemoveLogomarkDialog] = useState(false);
  const [showRemoveBrandColorDialog, setShowRemoveBrandColorDialog] = useState(false);
  const [showRemoveWebsiteDialog, setShowRemoveWebsiteDialog] = useState(false);
  const [showRemoveAppNameDialog, setShowRemoveAppNameDialog] = useState(false);
  const [showRemoveAppIconDialog, setShowRemoveAppIconDialog] = useState(false);
  const [showRemoveCampaignImageDialog, setShowRemoveCampaignImageDialog] = useState(false);
  const [showRemoveSmsPhoneDialog, setShowRemoveSmsPhoneDialog] = useState(false);
  const [showRemoveShortUrlDialog, setShowRemoveShortUrlDialog] = useState(false);

  return (
    <>
      <Stack>
        <Stack sx={{ p: '20px' }}>
          <Text category="title-small">
            Shared across programs
          </Text>
          <VSpacer size="5"/>
          <DescriptionCard
            description={description}
            onChange={(newDescription) => onChange({ description: newDescription || null })}
          />
          <VSpacer size="4"/>
          <Grid container spacing={2}>
            <Grid item sm={4} xs={12}>
              <BrandAssetsCard
                onAddClicked={() => setShowShortUrlModal(true)}
                onEditClicked={() => setShowShortUrlModal(true)}
                onRemoveClicked={() => {
                  if (retailer.appIcon) {
                    openSnackbar('Please remove the existing "App icon" first');
                    return;
                  }
                  setShowRemoveShortUrlDialog(true);
                }}
                statusText={retailer.shortUrlName}
                title="Short URL"
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <BrandAssetsCard
                onAddClicked={() => setShowAppNameModal(true)}
                onEditClicked={() => setShowAppNameModal(true)}
                onRemoveClicked={() => setShowRemoveAppNameDialog(true)}
                statusText={retailer.appName}
                title="App name"
              />
            </Grid>
            {showMessagingTelephoneInput && (
              <Grid item sm={4} xs={12}>
                <BrandAssetsCard
                  onAddClicked={() => setShowSmsPhoneModal(true)}
                  onEditClicked={() => setShowSmsPhoneModal(true)}
                  onRemoveClicked={() => setShowRemoveSmsPhoneDialog(true)}
                  statusText={retailer.messagingTelephone && (
                    formatPhoneNumber(retailer.messagingTelephone, 'paren')
                  )}
                  title="SMS phone number"
                />
              </Grid>
            )}
          </Grid>
          <VSpacer size="4"/>
          <TwilioDetails retailerId={retailer.id} />
          <VSpacer size="4"/>
          <BrandAssetsCardGroup
            assets={[
              {
                name: "Logo",
                value: retailer.image && getOriginalFileName(retailer.image),
              },
              {
                name: "Logomark",
                value: retailer.logomark && getOriginalFileName(retailer.logomark),
              },
              {
                name: "App icon",
                value: retailer.appIcon && getOriginalFileName(retailer.appIcon),
              },
            ]}
            onClick={(asset, mode) => {
              if (asset === "Logo") {
                mode === "delete"
                  ? setShowRemoveLogoDialog(true)
                  : setShowLogoModal(true);
              } else if (asset === "Logomark") {
                mode === "delete"
                  ? setShowRemoveLogomarkDialog(true)
                  : setShowLogomarkModal(true);
              } else if (asset === "App icon") {
                mode === "delete"
                  ? setShowRemoveAppIconDialog(true)
                  : setShowAppIconModal(true);
              }
            }}
            title="Shared image assets"
          />
        </Stack>

        <Stack>
          <ProgramSectionHeader name={agronomyProgramName} type={RewardsProgramType.Agronomy} />
          <Stack sx={{ p: '20px' }}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <BrandAssetsCard
                  onAddClicked={() => setShowWebsiteModal(true)}
                  onEditClicked={() => setShowWebsiteModal(true)}
                  onRemoveClicked={() => setShowRemoveWebsiteDialog(true)}
                  statusText={retailer.websiteUrl}
                  title="Retailer Website"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <BrandAssetsCard
                  color={retailer.brandColor}
                  onAddClicked={() => setShowBrandColorModal(true)}
                  onEditClicked={() => setShowBrandColorModal(true)}
                  onRemoveClicked={() => setShowRemoveBrandColorDialog(true)}
                  statusText={retailer.brandColor}
                  title="Brand Color"
                />
              </Grid>
            </Grid>
            <VSpacer size="4"/>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <BrandAssetsCardGroupItem
                  onClick={(_, mode) => (
                    mode === "delete"
                      ? setShowRemoveCampaignImageDialog(true)
                      : setShowCampaignImageModal(true)
                  )}
                  statusText={retailer.campaignImage && getOriginalFileName(retailer.campaignImage)}
                  title="Campaign image"
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Stack>
      {showLogoModal && (
        <ImageModal
          imageFilename={retailer.image}
          onClose={() => setShowLogoModal(false)}
          onSave={(image) => onChange({ image })}
          title="Upload Logo"
        />
      )}
      {showLogomarkModal && (
        <ImageModal
          imageFilename={retailer.logomark}
          onClose={() => setShowLogomarkModal(false)}
          onSave={(logomark) => onChange({ logomark })}
          title="Upload Logomark"
        />
      )}
      {showBrandColorModal && (
        <BrandColorModal
          brandColor={retailer.brandColor}
          onClose={() => setShowBrandColorModal(false)}
          onSave={(brandColor) => onChange({ brandColor })}
        />
      )}
      {showWebsiteModal && (
        <WebsiteModal
          onClose={() => setShowWebsiteModal(false)}
          onSave={(websiteUrl) => onChange({ websiteUrl })}
          url={retailer.websiteUrl}
        />
      )}
      {showAppNameModal && (
        <PropertyModal
          existingValue={retailer.appName}
          onClose={() => setShowAppNameModal(false)}
          onSave={(appName) => onChange({ appName })}
          propertyName="App name"
          validate={(value) => value.length <= 25}
        />
      )}
      {showAppIconModal && (
        <ImageModal
          acceptedFileTypes="PNG"
          acceptedMimeTypes="image/png"
          imageFilename={retailer.appIcon && `retailers/${retailer.shortUrlName}/${retailer.appIcon}`}
          onClose={() => setShowAppIconModal(false)}
          onSave={(appIcon) => onChange({ appIcon })}
          title="App icon"
        />
      )}
      {showCampaignImageModal && (
        <ImageModal
          imageFilename={retailer.campaignImage}
          maxSize={{
            height: 60,
            width: 360,
          }}
          onClose={() => setShowCampaignImageModal(false)}
          onSave={(campaignImage) => onChange({ campaignImage })}
          title="Campaign image"
        />
      )}
      {showSmsPhoneModal && (
        <PropertyModal
          existingValue={retailer.messagingTelephone?.slice(-10)}
          isPhoneNumber
          onClose={() => setShowSmsPhoneModal(false)}
          onSave={(messagingTelephone) => onChange({ messagingTelephone: `1${messagingTelephone}` })}
          propertyName="SMS phone number"
          validate={(value) => value.length === 10 && /^\d+$/.test(value)}
          validateOnSave={async (value) => {
            const isPhoneNumberOwned = await SmsApi.isPhoneNumberOwned(value);
            if (!isPhoneNumberOwned) {
              openSnackbar('SMS phone number must be owned in Twilio before it can be saved');
            }
            return isPhoneNumberOwned;
          }}
        />
      )}
      {showShortUrlModal && (
        <PropertyModal
          existingValue={retailer.shortUrlName}
          onClose={() => setShowShortUrlModal(false)}
          onSave={(shortUrlName) => onChange({ shortUrlName })}
          propertyName="Short URL name"
          validate={(value) => /^[a-zA-Z0-9-]+$/.test(value)}
          validateOnSave={async (shortUrlName) => {
            if (AppConfig.env.local) {
              return true;
            }

            const urlToTest = `https://${shortUrlName}.${AppConfig.shortUrlDomain}/`;
            const isReachable = await UtilityApi.getIsUrlReachable(urlToTest);
            if (!isReachable) {
              openSnackbar('Short url subdomain must be set-up before it can be saved');
            }
            return isReachable;
          }}
        />
      )}
      {showRemoveLogoDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the logo?
            </Text>
          }
          onCancel={() => setShowRemoveLogoDialog(false)}
          onConfirm={() => {
            onChange({ image: null });
            setShowRemoveLogoDialog(false);
          }}
          open
          testID="remove-logo-dialog"
        />
      )}
      {showRemoveLogomarkDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the logomark?
            </Text>
          }
          onCancel={() => setShowRemoveLogomarkDialog(false)}
          onConfirm={() => {
            onChange({ logomark: null });
            setShowRemoveLogomarkDialog(false);
          }}
          open
          testID="remove-logomark-dialog"
        />
      )}
      {showRemoveBrandColorDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the brand color?
            </Text>
          }
          onCancel={() => setShowRemoveBrandColorDialog(false)}
          onConfirm={() => {
            onChange({ brandColor: null });
            setShowRemoveBrandColorDialog(false);
          }}
          open
          testID="remove-brand-color-dialog"
        />
      )}
      {showRemoveCampaignImageDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the campaign image?
            </Text>
          }
          onCancel={() => setShowRemoveCampaignImageDialog(false)}
          onConfirm={() => {
            onChange({ campaignImage: null });
            setShowRemoveCampaignImageDialog(false);
          }}
          open
          testID="remove-campaign-image-dialog"
        />
      )}
      {showRemoveWebsiteDialog && (
        <ConfirmModal
          confirmButtonText="Yes, delete"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the retailer website?
            </Text>
          }
          onCancel={() => setShowRemoveWebsiteDialog(false)}
          onConfirm={() => {
            onChange({ websiteUrl: null });
            setShowRemoveWebsiteDialog(false);
          }}
          open
          testID="remove-website-dialog"
          title="Are you sure?"
        />
      )}
      {showRemoveAppNameDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the app name?
            </Text>
          }
          onCancel={() => setShowRemoveAppNameDialog(false)}
          onConfirm={() => {
            onChange({ appName: null });
            setShowRemoveAppNameDialog(false);
          }}
          open
          testID="remove-app-name-dialog"
        />
      )}
      {showRemoveAppIconDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the app icon?
            </Text>
          }
          onCancel={() => setShowRemoveAppIconDialog(false)}
          onConfirm={() => {
            onChange({ appIcon: null });
            setShowRemoveAppIconDialog(false);
          }}
          open
          testID="remove-app-icon-dialog"
        />
      )}
      {showRemoveSmsPhoneDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the SMS phone number?
            </Text>
          }
          onCancel={() => setShowRemoveSmsPhoneDialog(false)}
          onConfirm={() => {
            onChange({ messagingTelephone: null });
            setShowRemoveSmsPhoneDialog(false);
          }}
          open
          testID="remove-sms-phone-dialog"
        />
      )}
      {showRemoveShortUrlDialog && (
        <ConfirmModal
          confirmButtonText="Yes"
          message={
            <Text category="headline-small">
              Are you sure you want to remove the short URL name?
            </Text>
          }
          onCancel={() => setShowRemoveShortUrlDialog(false)}
          onConfirm={() => {
            onChange({ shortUrlName: null });
            setShowRemoveShortUrlDialog(false);
          }}
          open
          testID="remove-short-url-dialog"
        />
      )}
    </>
  );
};
